import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import type { Json } from "@/integrations/supabase/types";
import { fetchUserDataBatch } from "@/lib/user-data-helpers";
import { apiGet } from "@/lib/api-client";

export interface RecentPracticeSession {
    id: string;
    topic: string;
    slug: string;
    questions: Json;
    vocabulary: Json;
    speaking_guide?: Json;
    user_id: string;
    created_at: string;
    is_public?: boolean;
    is_pinned?: boolean;
    category?: string | null;
    banner_url?: string | null;
    last_practiced_at: string;
    creator_email?: string;
    creator_avatar_url?: string | null;
    creator_display_name?: string;
    view_count?: number;
    like_count?: number;
    comment_count?: number;
    bookmark_count?: number;
}

interface UseRecentPracticeOptions {
    limit?: number;
}

export const useRecentPractice = (userId: string | null, options: UseRecentPracticeOptions = {}) => {
    const { limit = 5 } = options;

    return useQuery({
        queryKey: queryKeys.recentPractice.list(userId, limit),
        enabled: !!userId,
        queryFn: async (): Promise<RecentPracticeSession[]> => {
            if (!userId) return [];

            // Server-side: distinct recent sessions with MAX(updated_at), limited.
            // (was: fetch every practice_responses row, group + slice client-side.)
            // `get_recent_practice_sessions` does not exist; this is the same
            // query, served by a typed route.
            const data = await apiGet<unknown[]>(`/v1/sessions/recent?limit=${limit}`);

            if (!data || data.length === 0) {
                return [];
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sortedSessions: RecentPracticeSession[] = (data as any[]).map((row) => ({
                id: row.id,
                topic: row.topic,
                slug: row.slug,
                // Count placeholders — list cards only render `.length`.
                questions: Array.from({ length: row.question_count ?? 0 }),
                vocabulary: Array.from({ length: row.vocabulary_count ?? 0 }),
                user_id: row.user_id,
                created_at: row.created_at,
                is_public: row.is_public,
                is_pinned: row.is_pinned,
                category: row.category_name || null,
                banner_url: row.banner_url || null,
                last_practiced_at: row.last_practiced_at,
                view_count: row.view_count || 0,
                like_count: row.like_count || 0,
                comment_count: row.comment_count || 0,
                bookmark_count: row.bookmark_count || 0,
            }));

            // Fetch user data (email and avatar) for all sessions
            const userIds = [...new Set(sortedSessions.map((s) => s.user_id))];
            const userDataMap = await fetchUserDataBatch(userIds);

            // Add creator email and avatar to each session
            const sessionsWithCreator = sortedSessions.map((session) => {
                const userData = userDataMap.get(session.user_id);
                return {
                    ...session,
                    creator_email: userData?.email || "Unknown",
                    creator_avatar_url: userData?.avatar_url || null,
                    creator_display_name: userData?.display_name || "Unknown",
                };
            });

            return sessionsWithCreator;
        },
        staleTime: 1000 * 60 * 2, // Cache for 2 minutes
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus: false,
    });
};
